///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Transient glass toast shown after saving a view (Bookmark button / ⌘D).
/// The auto-generated name arrives pre-selected for zero-friction inline
/// rename, Safari-bookmark style: press Enter (or just ignore it) and it
/// slides away.
///

import { useIsMobile } from "@/components/utils/use-mobile"
import { setSavedViewToast } from "@/redux/dialogSlice"
import { renameView } from "@/redux/savedViewsSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { BookmarkIcon, XIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { slotDismissClass, slotPillClass, slotPriority, useNotificationSlot } from "./NotificationSlot"

const cAutoDismissMs = 6000

export function SaveViewToast() {
	const dispatch = useAppDispatch()
	const viewId = useAppSelector(state => state.dialog.savedViewToastId)
	const view = useAppSelector(state => viewId ? state.savedViews.views[viewId] : undefined)

	const [name, setName] = useState("")
	const inputRef = useRef<HTMLInputElement>(null)
	const interactedRef = useRef(false)
	const isMobile = useIsMobile()
	const visible = useNotificationSlot("save-view-toast", !!viewId && !!view, slotPriority.saveViewToast)

	// Seed the input and select the name whenever a new view is saved
	useEffect(() => {
		if (!view) return
		setName(view.name)
		interactedRef.current = false
		// Desktop: select after the element mounts so typing replaces the
		// auto-name. Mobile: skip the programmatic focus — the save gesture
		// has long ended (the auto-name lookup is async), so iOS suppresses
		// the keyboard and leaves the field in a focused-but-keyboardless
		// state where a follow-up tap won't re-summon it. Letting the user's
		// own tap do the focusing brings the keyboard up reliably, and
		// select-on-focus keeps the type-to-replace behavior.
		if (!isMobile) {
			requestAnimationFrame(() => {
				inputRef.current?.focus()
				inputRef.current?.select()
			})
		}
		// Only re-run when a different view is saved, not on rename
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [view?.id])

	// Auto-dismiss unless the user started editing
	useEffect(() => {
		if (!viewId) return
		const timer = setTimeout(() => {
			if (!interactedRef.current) {
				dispatch(setSavedViewToast(null))
			}
		}, cAutoDismissMs)
		return () => clearTimeout(timer)
	}, [viewId, dispatch])

	if (!viewId || !view || !visible) return null

	const commit = () => {
		if (name.trim().length > 0 && name.trim() !== view.name) {
			dispatch(renameView({ id: view.id, name }))
		}
	}

	const close = () => {
		commit()
		dispatch(setSavedViewToast(null))
	}

	return (
		<div
			// Any tap/click in the toast counts as interaction and stops the
			// auto-dismiss (a mobile user may still be typing at the 6s mark).
			// Desktop's programmatic focus doesn't fire pointerdown, so the
			// untouched toast still dismisses itself there.
			onPointerDown={() => { interactedRef.current = true }}
			className={cn(slotPillClass, "pr-2")}
		>
			<BookmarkIcon className="w-4 h-4 text-purple-300 shrink-0" />
			<span className="text-white/60 text-sm shrink-0">View saved</span>
			<input
				ref={inputRef}
				type="text"
				value={name}
				enterKeyHint="done"
				onChange={(e) => {
					interactedRef.current = true
					setName(e.target.value)
				}}
				onFocus={(e) => {
					// Defer past the browser's own post-focus caret placement,
					// which would override an immediate select()
					const input = e.target
					setTimeout(() => input.select(), 0)
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						close()
					} else if (e.key === "Escape") {
						dispatch(setSavedViewToast(null))
					}
					e.stopPropagation()
				}}
				onBlur={commit}
				className={cn(
					// 16px on mobile so iOS doesn't zoom the page into the input
					"bg-white/10 text-white text-[16px] sm:text-sm rounded-md px-2 py-1 w-52",
					"border border-white/10 focus:border-purple-400/50",
					"focus:outline-none placeholder-white/30"
				)}
				aria-label="Saved view name"
			/>
			<button
				onClick={close}
				className={slotDismissClass}
				aria-label="Dismiss"
			>
				<XIcon className="w-4 h-4" />
			</button>
		</div>
	)
}
